/**
 * 🧲 Lazy, fetch-once loaders for GameStepPage panels (09-24, sweep #151 follow-up).
 *
 * Why this exists: the Memory Wall and the Recap archive each fetched in an
 * effect that listed its OWN `loading` flag as a dependency. The effect set
 * loading: true, that re-ran it, and React runs the previous run's cleanup
 * first (cancelled = true), so the answer was thrown away and the panel sat on
 * "Checking the wall..." forever (true since 08-06; measured under React 19).
 *
 * Here the in-flight guard is a REF, so marking the panel loading never re-runs
 * the effect. A real change (the panel closes, another space) still cancels,
 * and the next open fetches again. `load` / `toState` / `failState` must be
 * stable (module level): they are dependencies, and a new function every
 * render would restart the fetch every render.
 */
import { useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config/api';

export function useLazyOnceFetch({ wanted, key, loaded, setState, load, toState, failState }) {
  const inFlight = useRef(false);
  useEffect(() => {
    if (!wanted || !key || loaded || inFlight.current) return undefined;
    inFlight.current = true;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    (async () => {
      try {
        const data = await load(key);
        if (!cancelled) setState(toState(data));
      } catch (e) {
        if (!cancelled) setState(failState);
      }
    })();
    return () => { cancelled = true; inFlight.current = false; };
  }, [wanted, key, loaded, setState, load, toState, failState]);
}

const getJson = async (url) => (await fetch(url)).json();

// 📌 The Memory Wall (+ the house day and the "Older memories" summary; an older
// backend sends neither and the book button stays hidden).
const loadHouseWall = (gameId) => getJson(`${API_BASE_URL}/api/takeGameStep?houseMemoryWall=true&gameInstanceId=${gameId}`);
export const houseWallFromResponse = (data) => ({
  loading: false,
  loaded: true,
  memories: Array.isArray(data?.memories) ? data.memories : [],
  scenes: Array.isArray(data?.scenes) ? data.scenes : [],
  pollHistory: Array.isArray(data?.poll_history) ? data.poll_history : [],
  houseToday: data?.house_today || null,
  archive: data?.archive || null,
  // 🗝️ 10-14 the "Open mysteries" card: only when the backend sends it (HOUSE_MYSTERY_CARD_ENABLED)
  ...(Array.isArray(data?.threads) ? { threads: data.threads } : {}),
});
const HOUSE_WALL_FAILED = { loading: false, loaded: true, memories: [], scenes: [], pollHistory: [] };

export function useHouseWallFetch({ wanted, gameId, loaded, setHouseWall }) {
  useLazyOnceFetch({
    wanted, key: gameId, loaded, setState: setHouseWall,
    load: loadHouseWall, toState: houseWallFromResponse, failState: HOUSE_WALL_FAILED,
  });
}

// 📼 The Recap archive. Fail SOFT: an empty archive degrades to the rolling
// window, never to a broken lane.
const loadRecapHistory = (gameId) => getJson(`${API_BASE_URL}/api/takeGameStep?getRecapHistory=true&gameInstanceId=${gameId}`);
export const recapHistoryFromResponse = (data) => ({
  loading: false,
  loaded: true,
  actions: Array.isArray(data?.actions) ? data.actions : [],
  counts: data?.counts || null,
});
const RECAP_HISTORY_FAILED = { loading: false, loaded: true, actions: [], counts: null };

export function useRecapHistoryFetch({ wanted, gameId, loaded, setRecapHistory }) {
  useLazyOnceFetch({
    wanted, key: gameId, loaded, setState: setRecapHistory,
    load: loadRecapHistory, toState: recapHistoryFromResponse, failState: RECAP_HISTORY_FAILED,
  });
}
